import React, { useState, useEffect } from 'react';
import { Row, Col, Select, Card, Form, Space, Empty, Spin } from 'antd';
import useDebouncedSearch from '@/hooks/useDebouncedSearch';
import moment from 'moment';
import styles from '@/common/Layout/index.module.less';
import FormItem from '@/common/FormItem';
import CustomTable from '@/common/Content/CustomTable';
import Layout from '@/common/Layout';
import { useDispatch, useSelector } from 'react-redux';
import { getAllMaterialslist } from '@/features/stockCard/redux/slice';
import { getAllBinPositions } from '../../redux/slice';
import CustomButton from '@/common/CustomButton';
import { getLoggedInUser } from 'utilities/helpers';
import { getProducts } from '@/features/productionLabelGen/redux/slice';

const TrackBinPage = () => {
  const [form] = Form.useForm();
  const { allMaterials } = useSelector((state) => state.stocks);

  const dispatch = useDispatch();
  const [batches, setBatches] = useState([]);
  const [containers, setContainers] = useState([]);
  const [data, setData] = useState([]);
  console.log('state.staocks ', allMaterials);

  const { user } = getLoggedInUser();
  // Production bin flow covers both production roles. The backend branches on
  // prod_user || prod_ebmr, so the UI must match or a prod_user gets the
  // warehouse screens over the production controller branch.
  const isProdRole = ['prod_user', 'prod_ebmr'].includes(user?.role);

  // Load the initial option list once. This used to also depend on [search, code],
  // which fired a fresh request on every keystroke AND refetched the same list
  // when a material was picked — the picked-material case duplicated the request
  // onChange already makes.
  useEffect(() => {
    handleGetAllMaterialsList('');
  }, []);

  const handleGetAllMaterialsList = (searchValue = '') => {
    let payload = { search: searchValue };
    return isProdRole
      ? dispatch(getProducts(payload)).then(function (response) {
          if (response?.payload?.success) {
            setData(response?.payload?.data);
          }
        })
      : dispatch(getAllMaterialslist(payload));
  };

  // Debounced option lookup with a spinner that stays up until the request
  // settles. See hooks/useDebouncedSearch — the standard for API-backed Selects.
  const { handleSearch: handleMaterialSearch, loading: materialSearchLoading } =
    useDebouncedSearch((value) => handleGetAllMaterialsList(value));

  // const handleGetAllMaterialsList = () => {
  //   let payload = { search };
  //   dispatch(getAllMaterialslist(payload));
  // };

  const onChange = (value, type) => {
    form.resetFields(['in_house_batch_number']);
    const material = allMaterials.filter(
      (MaterialsList) => MaterialsList.id === value
    );

    let payload = { search: material[0].code };
    dispatch(getAllMaterialslist(payload)).then((response) => {
      if (response?.payload?.success) {
        // resetField('in_house_batch_number');

        setBatches(response?.payload?.data[0]?.batch_details);
        dispatch(getAllMaterialslist());
      }
    });
    form.setFieldsValue({ supplier_id: null });
    type == 'id'
      ? form.setFieldsValue({ material_name: value })
      : form.setFieldsValue({ material_id: value });
  };

  const onFinish = (values) => {
    console.log(values);
  };

  const onChangeBatch = (value) => {
    // const batches_details =
    //   value == 'all'
    //     ? allMaterials[0]?.batch_details
    //     : batches.filter((batches) => batches.id === value);
    // setBatches(batches_details);
    const payload = { batch_details_id: value };
    dispatch(getAllBinPositions(payload)).then((response) => {
      if (response?.payload?.success) {
        // resetField('in_house_batch_number');
        setContainers(response?.payload?.data);
        // setBatches(response?.payload?.data[0]?.batch_details);
        // dispatch(getAllMaterialslist());
      }
    });
  };
  // The In House Batch Number options are already loaded client-side, so the
  // Select filters them locally via optionFilterProp="children". The former
  // onSearchBatch handler read `batches.inhouse_batch_number` off an ARRAY —
  // always undefined, so .toLowerCase() threw on every keystroke — and then
  // replaced the options array with a boolean. Removed rather than repaired.

  // Weight columns are STRING in prod_dispensing_labels, so legacy rows can hold
  // '', null or the literal 'NaN' — treat all three as "no weight captured".
  const displayWeight = (weight) =>
    weight != null && weight !== '' && weight !== 'NaN' ? weight : null;

  const isProd = isProdRole;
  const binColumns = isProd
    ? [
        {
          title: 'Bin Number',
          key: 'bin_number',
          ellipsis: true,
          render: (_, record) => record?.bin?.bin_number || '-'
        },
        {
          title: 'Container No.',
          key: 'container',
          ellipsis: true,
          render: (_, record) =>
            record?.prod_dispensing_label?.prod_containers || '-'
        },
        {
          title: 'Area',
          key: 'area',
          ellipsis: true,
          render: (_, record) => record?.area_name || '-'
        },
        {
          title: 'Room',
          key: 'room',
          ellipsis: true,
          render: (_, record) => record?.room_no || '-'
        },
        {
          title: 'Gross Qty',
          key: 'gross_qty',
          ellipsis: true,
          render: (_, record) =>
            displayWeight(record?.prod_dispensing_label?.gross_weight) || '-'
        },
        {
          title: 'UOM',
          key: 'uom',
          ellipsis: true,
          // WIP containers carry no UOM field of their own — every prod label
          // surface (labels, PDFs) prints these weights in KG.
          render: (_, record) =>
            displayWeight(record?.prod_dispensing_label?.gross_weight)
              ? 'KG'
              : '-'
        },
        {
          title: 'Status',
          key: 'status',
          ellipsis: true,
          render: (_, record) =>
            record?.prod_dispensing_label?.is_outward
              ? 'Outwarded'
              : record?.prod_dispensing_label?.prod_status || '-'
        }
      ]
    : [
        // One row per placement EVENT, not per bin — so a bin that was topped up
        // reads "20 by EMP01 on the 2nd, then 60 by EMP07 on the 3rd" instead of
        // a bare total of 80 with no idea who put what there when.
        {
          title: 'Location',
          key: 'location',
          ellipsis: true,
          // Bin numbers are zone-letter-rack-position (e.g. 5-J-10-12) with
          // multi-digit segments, so a plain string compare puts 4-K-10-09
          // before 4-K-2-09. Numeric collation orders the segments as numbers.
          sorter: (a, b) =>
            (a?.location || '').localeCompare(b?.location || '', undefined, {
              numeric: true,
              sensitivity: 'base'
            }),
          render: (_, record) => record?.location || '-'
        },
        {
          title: 'Qty Placed',
          key: 'qty',
          ellipsis: true,
          sorter: (a, b) => Number(a?.qty ?? 0) - Number(b?.qty ?? 0),
          // `??` not `||` — a captured qty of 0 must render as 0. Placements made
          // before quantity capture have no value and show '-'.
          render: (_, record) => record?.qty ?? '-'
        },
        {
          title: 'UOM',
          key: 'uom',
          ellipsis: true,
          // Derived from the material master, never stored on the placement.
          render: (_, record) => record?.uom || '-'
        },
        {
          title: 'Placed By',
          key: 'employee_id',
          ellipsis: true,
          render: (_, record) => record?.employee_id || '-'
        },
        {
          title: 'Placed On',
          key: 'event_date',
          ellipsis: true,
          sorter: (a, b) =>
            new Date(a?.event_date || 0) - new Date(b?.event_date || 0),
          render: (_, record) =>
            record?.event_date
              ? moment(record.event_date).format('DD MMM YYYY HH:mm')
              : '-'
        }
      ];

  const binLocale = {
    // Once a batch is chosen, an empty table means it is in no bin right now
    // (fully dispensed or sampled) — not that nothing has been selected.
    emptyText: (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span>
            {batches?.length
              ? 'This batch is not currently in any bin'
              : 'Select a batch to see its bin locations'}
          </span>
        }
      />
    )
  };

  return (
    <Layout sider={false}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Card title="MATERIAL BIN LOCATION" className={styles.CustomPanel}>
          {!isProdRole ? (
            <Row gutter={24}>
              <Col xs={24} xl={8} span={12} md={24} sm={24}>
                <FormItem
                  label="Material Code"
                  name="material_id"
                  rules={[
                    {
                      required: true,
                      message: 'Select Material Code'
                    }
                  ]}
                  className={styles.customLable}
                >
                  <Select
                    showSearch
                    placeholder="Select a material code"
                    optionFilterProp="children"
                    onChange={(e) => onChange(e, 'id')}
                    onSearch={handleMaterialSearch}
                    loading={materialSearchLoading}
                    filterOption={false}
                    notFoundContent={
                      materialSearchLoading ? <Spin size="small" /> : null
                    }
                  >
                    {allMaterials?.map((item, index) => {
                      return (
                        <option value={item.id} key={index}>
                          {item.code}
                        </option>
                      );
                    })}
                  </Select>
                </FormItem>
              </Col>
              <Col xs={24} xl={8} span={12} md={24} sm={24}>
                <FormItem
                  label="Material Name"
                  name="material_name"
                  rules={[
                    {
                      required: false,
                      message: 'Select Material Name'
                    }
                  ]}
                  className={styles.customLable}
                >
                  <Select
                    showSearch
                    placeholder="Select a material name"
                    optionFilterProp="children"
                    onChange={(e) => onChange(e, 'name')}
                    onSearch={handleMaterialSearch}
                    loading={materialSearchLoading}
                    filterOption={false}
                    notFoundContent={
                      materialSearchLoading ? <Spin size="small" /> : null
                    }
                  >
                    {allMaterials?.map((item, index) => {
                      return (
                        <option value={item.id} key={index}>
                          {item.name}
                        </option>
                      );
                    })}
                  </Select>
                  {/* <Input value={`${code?.name || 'NA'}`} disabled /> */}
                </FormItem>
              </Col>
              <Col xs={24} xl={8} span={12} md={24} sm={24}>
                <FormItem
                  label="In House Batch Number"
                  name="in_house_batch_number"
                  rules={[
                    {
                      required: true,
                      message: 'Select In House Batch Number'
                    }
                  ]}
                  className={styles.customLable}
                >
                  <Select
                    showSearch
                    placeholder="Select In House Batch Number"
                    optionFilterProp="children"
                    onChange={onChangeBatch}
                  >
                    <option value="all">All</option>
                    {batches?.map((item, index) => {
                      return (
                        <option value={item.id} key={index}>
                          {item.inhouse_batch_number}
                        </option>
                      );
                    })}
                  </Select>
                </FormItem>
              </Col>
            </Row>
          ) : (
            <Row gutter={24}>
              <Col xs={24} xl={8} span={12} md={24} sm={24}>
                <FormItem
                  label="Product Batch Number (Process › SubProcess - LotNo - NextStage)"
                  name="in_house_batch_number"
                  rules={[
                    {
                      required: true,
                      message: 'Select In House Batch Number'
                    }
                  ]}
                  className={styles.customLable}
                >
                  <Select
                    showSearch
                    placeholder="Select In Product Batch Number"
                    optionFilterProp="children"
                    onChange={onChangeBatch}
                    onSearch={handleMaterialSearch}
                    loading={materialSearchLoading}
                    filterOption={false}
                    notFoundContent={
                      materialSearchLoading ? <Spin size="small" /> : null
                    }
                  >
                    <option value="all">All</option>
                    {data?.map((item, index) => {
                      const dispense_process =
                        item?.dispensing_request?.prod_label_process_master
                          ?.process_label_name ||
                        // WIP-only stages (compression/inspection) have no
                        // dispensing request; use the record's own process.
                        item?.prod_label_process_master?.process_label_name;
                      const dispense_sub_process =
                        item?.dispensing_request?.prod_label_sub_process
                          ?.sub_process_name;
                      const process_label =
                        dispense_process && dispense_sub_process
                          ? `${dispense_process} › ${dispense_sub_process}`
                          : dispense_process || null;
                      const parts = [
                        item?.batch_no,
                        process_label,
                        item?.lot_no ? `lot ${item.lot_no}` : null,
                        item?.prodlabel_nextstage_master?.prodlabel_nextstage
                      ].filter(Boolean);
                      return (
                        <option value={item.id} key={index}>
                          {parts.length ? parts.join(' - ') : item?.batch_no}
                        </option>
                      );
                    })}
                  </Select>
                </FormItem>
              </Col>
            </Row>
          )}
        </Card>
        <br />
        <Card title="BIN LOCATIONS" className={styles.CustomPanel}>
          <CustomTable
            columns={binColumns}
            dataSource={containers}
            rowKey={(record) => record.id}
            pagination={false}
            scroll={{ x: 'auto' }}
            locale={binLocale}
          />
        </Card>
        <br />
        <FormItem className="text-center">
          <Space>
            <CustomButton
              className={styles.inwardButton}
              type="primary"
              ghost
              onClick={() => history.back()}
            >
              Back
            </CustomButton>
          </Space>
        </FormItem>
      </Form>
    </Layout>
  );
};
export default TrackBinPage;
